// Sprint UI-6 Phase B → rate-limit-banner-ssot — Network & system banners.
//
// Renders at the very top of the page (above/inside Topbar slot) when the
// app is in a degraded state. Only one banner shows at a time, prioritised:
//
//   1. disconnected  → red:   "Disconnected — agents keep running…" + Retry
//   2. reconnecting  → amber: "Reconnecting" + spinner
//   3. rateLimit     → amber: "Hit Claude Max <window> limit on <host>…" + View usage
//   4. memoryConstrained → amber: "Server memory is tight…"
//
// All copy via i18n (Layer 7 §4.3.2).
//
// Sprint rate-limit-banner-ssot — banner state derives from the backend
// `rate_limit_state` socket event, which the RateLimitBroadcaster emits
// from real Anthropic usage. No more user dismiss: the banner is the
// truth of the cap, not a notification. "View usage" opens the modal
// (same data, fuller breakdown).

import { useEffect } from 'react';
import { useAppStore } from '../../stores/app.store';
import { getSocket } from '../../lib/socket';
import { useTranslation } from '../../i18n';
import { Spinner } from '../atoms/Spinner';
import { fetchRunnersUsage } from '../../lib/api';
import type { RateLimitOffender, RateLimitWindow } from '../../types';

export function NetworkBanner() {
  const connectionStatus = useAppStore((s) => s.connectionStatus);
  const memoryConstrained = useAppStore((s) => s.concurrency.memoryConstrained);
  const rateLimitState = useAppStore((s) => s.rateLimitState);

  if (connectionStatus === 'disconnected') return <DisconnectedBanner />;
  if (connectionStatus === 'reconnecting') return <ReconnectingBanner />;
  if (rateLimitState.active) return <RateLimitBanner offenders={rateLimitState.runners} />;
  if (memoryConstrained) return <MemoryConstrainedBanner />;

  return null;
}

function ReconnectingBanner() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 px-4 py-1.5 pt-safe-top box-content bg-status-warning/15 text-status-warning text-body border-b border-status-warning/30"
      data-testid="network-banner-reconnecting"
    >
      <Spinner size="sm" />
      <span>{t('networkBanner.reconnecting')}</span>
    </div>
  );
}

function DisconnectedBanner() {
  const { t } = useTranslation();

  function handleRetry() {
    try {
      getSocket().connect();
    } catch {
      // singleton may not exist; ignore
    }
  }

  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-3 px-4 py-1.5 pt-safe-top box-content bg-status-error/15 text-body border-b border-status-error/30"
      data-testid="network-banner-disconnected"
    >
      <span className="text-foreground">{t('networkBanner.disconnected')}</span>
      <button
        type="button"
        onClick={handleRetry}
        className="px-3 py-1 rounded-md bg-status-error/20 text-status-error hover:bg-status-error/30 text-caption font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-status-error/40"
        data-testid="network-banner-retry"
      >
        {t('networkBanner.disconnectedRetry')}
      </button>
    </div>
  );
}

// Map a wire bucket name to the i18n window label.
function windowLabel(t: ReturnType<typeof useTranslation>['t'], w: RateLimitWindow): string {
  switch (w) {
    case 'five_hour':
      return t('networkBanner.windowFiveHour');
    case 'seven_day':
      return t('networkBanner.windowSevenDay');
    case 'seven_day_sonnet':
      return t('networkBanner.windowSevenDaySonnet');
    case 'seven_day_opus':
      return t('networkBanner.windowSevenDayOpus');
    case 'extra_usage':
      return t('networkBanner.windowExtraUsage');
  }
}

function pickHeadline(offenders: RateLimitOffender[]): {
  headline: RateLimitOffender;
  distinctRunners: number;
} {
  // Most urgent = earliest resetsAt.
  let headline = offenders[0]!;
  let headlineMs = Date.parse(headline.resetsAt);
  for (const o of offenders) {
    const ms = Date.parse(o.resetsAt);
    if (Number.isFinite(ms) && (!Number.isFinite(headlineMs) || ms < headlineMs)) {
      headline = o;
      headlineMs = ms;
    }
  }
  const distinctRunners = new Set(offenders.map((o) => o.runnerId)).size;
  return { headline, distinctRunners };
}

function RateLimitBanner({ offenders }: { offenders: RateLimitOffender[] }) {
  const { t } = useTranslation();
  const setUsageModalOpen = useAppStore((s) => s.setUsageModalOpen);

  // Sprint rate-limit-banner-ssot (RLB-4) — defensive lazy poll while the
  // banner is active. The backend broadcaster ticks every 60 s already, so
  // this is a belt-and-suspenders refetch ensuring the local socket also
  // catches the next cleared emission even if it briefly disconnects.
  useEffect(() => {
    const ac = new AbortController();
    const id = window.setInterval(() => {
      void fetchRunnersUsage({ fresh: true, signal: ac.signal }).catch(() => {
        // Silent — banner state remains last-known; broadcaster will
        // self-correct on next emission.
      });
    }, 60_000);
    return () => {
      window.clearInterval(id);
      ac.abort();
    };
  }, []);

  if (offenders.length === 0) return null;

  const { headline, distinctRunners } = pickHeadline(offenders);
  const time = new Date(headline.resetsAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const windowText = windowLabel(t, headline.window);

  let copy: string;
  if (distinctRunners === 1) {
    copy = t('networkBanner.claudeMaxOneRunner', {
      window: windowText,
      hostname: headline.hostname,
      time,
    });
  } else {
    // We don't know the total runner count on the frontend here; "all" vs
    // "some" requires a server-side hint. Defensive: treat any >1 as
    // "some N runners". When the broadcaster covers every runner the
    // count itself communicates the scope (Santi reads "all 4" from "4").
    copy = t('networkBanner.claudeMaxSomeRunners', {
      window: windowText,
      count: distinctRunners,
      time,
    });
  }

  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-3 px-4 py-1.5 pt-safe-top box-content bg-status-warning/15 text-status-warning text-body border-b border-status-warning/30"
      data-testid="network-banner-rate-limit"
    >
      <span>{copy}</span>
      <button
        type="button"
        onClick={() => setUsageModalOpen(true)}
        className="px-3 py-1 rounded-md bg-status-warning/20 text-status-warning hover:bg-status-warning/30 text-caption font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-status-warning/40"
        data-testid="network-banner-rate-limit-view-usage"
      >
        {t('networkBanner.viewUsage')}
      </button>
    </div>
  );
}

function MemoryConstrainedBanner() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      className="flex items-center justify-center gap-2 px-4 py-1.5 pt-safe-top box-content bg-status-warning/15 text-status-warning text-body border-b border-status-warning/30"
      data-testid="network-banner-memory"
    >
      <span>{t('networkBanner.memoryConstrained')}</span>
    </div>
  );
}
